import { Box, Center, Image } from '@mantine/core'
import { useViewportSize } from '@mantine/hooks'
import ImageGallery, {
  GalleryItem as ImageGalleryItem
} from 'react-image-gallery'
import ImagePlaceholder from '@/assets/image-placeholder.png'
import { useDeviceDetect } from '@/hooks/useDeviceDetect'
import { FileMetadata } from '@/types/dataset'

const THUMBNAIL_SIZE = 90

// Limit thumbnails to 100 items for performance reasons.
// Virtual scrolling is not supported by react-image-gallery.
// See https://github.com/xiaolin/react-image-gallery/issues/765#issuecomment-1731553238
const THUMBNAIL_DISPLAY_LIMIT = 100

type MediaItemProps = {
  file: FileMetadata
  withThumbnails: boolean
}

const MediaItem = ({ file, withThumbnails }: MediaItemProps) => {
  const { isMobile } = useDeviceDetect()

  const { height: vHeight } = useViewportSize()

  const verticalOffset = withThumbnails ? 220 : 100

  return (
    <Box px={isMobile ? 60 : 120} h={vHeight - verticalOffset}>
      <Center h="100%">
        {file.media_type === 'video' ? (
          <video
            style={{ maxHeight: vHeight - 2 * verticalOffset, width: '100%' }}
            controls
          >
            <source src={file.media_file} type="video/mp4" />
          </video>
        ) : (
          <Image
            src={file.media_file}
            fit="contain"
            h={vHeight - 2 * verticalOffset}
          />
        )}
      </Center>
    </Box>
  )
}

type GalleryItem = ImageGalleryItem & {
  file: FileMetadata
}

type MediaGalleryProps = {
  files: FileMetadata[]
  activeFile: FileMetadata | null
  showThumbnails?: boolean
  onSlideChange: (file: FileMetadata) => void
}

export const MediaGallery = ({
  files,
  activeFile,
  showThumbnails = false,
  onSlideChange
}: MediaGalleryProps) => {
  const items: GalleryItem[] = files.map((file) => ({
    original: file.media_file,
    thumbnail: file.thumbnail,
    file,
    thumbnailWidth: THUMBNAIL_SIZE,
    thumbnailHeight: THUMBNAIL_SIZE
  }))

  const hasThumbnails =
    showThumbnails && items.length <= THUMBNAIL_DISPLAY_LIMIT

  const startIndex = Math.max(
    0,
    items.findIndex((item) => item.file.id === activeFile?.id)
  )

  return (
    <Box px={24}>
      <ImageGallery
        items={items}
        startIndex={startIndex}
        infinite={false}
        showThumbnails={hasThumbnails}
        showPlayButton={false}
        showFullscreenButton={false}
        renderItem={(item) => {
          // it's important to use item attached to gallery data, otherwise
          // media is not rendered properly
          const { file } = item as GalleryItem
          return <MediaItem file={file} withThumbnails={hasThumbnails} />
        }}
        useBrowserFullscreen={false}
        lazyLoad
        onErrorImageURL={ImagePlaceholder}
        onSlide={(index) => onSlideChange(items[index].file)}
      />
    </Box>
  )
}
